import { getEventHash } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

export interface UnsignedEvent {
  kind: number;
  content: string;
  tags: string[][];
  created_at: number;
  pubkey: string;
}

/**
 * Counts the number of leading zero bits in a hex-encoded string
 * (the event id, per NIP-13).
 */
export function countLeadingZeroBits(hexId: string): number {
  let count = 0;
  for (let i = 0; i < hexId.length; i++) {
    const nibble = parseInt(hexId[i], 16);
    if (Number.isNaN(nibble)) return count;
    if (nibble === 0) {
      count += 4;
    } else {
      // Count leading zeros within this nibble
      if (nibble < 2) count += 3;
      else if (nibble < 4) count += 2;
      else if (nibble < 8) count += 1;
      break;
    }
  }
  return count;
}

/** Extracts the nonce tag data from an event, if present. */
export function getNonce(e: NostrEvent): { nonce: string; difficulty: number } | undefined {
  const nonceTag = e.tags.find(([t]) => t === 'nonce');
  if (!nonceTag || nonceTag.length < 3) return undefined;
  return { nonce: nonceTag[1], difficulty: Number(nonceTag[2]) };
}

/**
 * Verifies an event meets a minimum PoW difficulty (NIP-13).
 * The event id hash already includes the nonce tag, so we just
 * count leading zero bits of the id.
 */
export function verifyPow(e: NostrEvent, min: number): boolean {
  if (e.kind === 0) return true; // never require PoW for profiles
  const nonce = getNonce(e);
  if (!nonce) return false;
  if (!Number.isFinite(nonce.difficulty)) return false;
  return countLeadingZeroBits(e.id) >= min;
}

/**
 * Mines PoW for an unsigned event at the given difficulty (NIP-13).
 * Yields to the event loop every 1000 tries so the UI stays responsive.
 * Returns the final tags array including the winning nonce tag.
 */
export async function minePow(
  unsigned: UnsignedEvent,
  difficulty: number,
  onProgress?: (tries: number) => void,
  shouldCancel?: () => boolean,
): Promise<string[][]> {
  const tagsWithoutNonce = unsigned.tags.filter(([t]) => t !== 'nonce');
  let tries = 0;

  for (;;) {
    const tags = [...tagsWithoutNonce, ['nonce', String(tries), String(difficulty)]];
    const id = getEventHash({ ...unsigned, tags });
    if (countLeadingZeroBits(id) >= difficulty) {
      return tags;
    }
    tries++;
    if (tries % 1000 === 0) {
      onProgress?.(tries);
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (shouldCancel?.()) {
        throw new Error('Mining cancelled');
      }
    }
  }
}
